import { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { API } from "@/lib/api";
import {
  MagnifyingGlass, FilePdf, Link as LinkIcon, BookOpen, ArrowUp,
} from "@phosphor-icons/react";

/**
 * Help — The User's Guide reading experience (v3.7 audit + Tier-3 docs).
 *
 * Reads /help/USER_GUIDE.md at runtime so future edits land instantly. The
 * sidebar lists every H2 anchor; clicking an item scrolls + updates the
 * URL hash so links are shareable. A small client-side search filters the
 * sidebar by section title for fast nav inside a long document.
 */

// Short-form anchors used by HelpLink (?) icons and WelcomeTour deep-links.
// Map them to the long slugs derived from the H2 headings so existing call
// sites keep working when the guide is rewritten.
const ANCHOR_ALIASES = {
  "what-this-is": "what-this-is-and-what-it-is-not",
  "first-ten": "your-first-ten-minutes",
  "organised": "how-the-ledger-is-organised",
  "six-doors": "recording-an-asset-the-six-doors",
  "asset-record": "the-asset-record-up-close",
  "classes": "asset-classes-the-vocabulary-of-your-collection",
  "leo": "leo-your-private-estate-analyst",
  "maintenance": "maintenance-turning-chores-into-a-record",
  "vault": "the-vault-every-file-in-one-drawer",
  "households": "households-sharing-the-ledger-with-people-you-trust",
  "heirs": "sharing-with-heirs-the-controlled-disclosure-link",
  "manifest": "the-estate-manifest-pdf",
  "settings": "settings-account",
  "faq": "practical-answers-to-common-questions",
  "glossary": "a-short-glossary",
};

function resolveAnchor(a) {
  return ANCHOR_ALIASES[a] || a;
}

export default function Help() {
  const [md, setMd] = useState("");
  const [q, setQ] = useState("");
  const [activeAnchor, setActiveAnchor] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    fetch("/help/USER_GUIDE.md")
      .then((r) => r.text())
      .then(setMd)
      .catch(() => setMd("# Guide unavailable\n\nPlease try again in a moment."));
  }, []);

  // Build the section index from the markdown
  const sections = useMemo(() => parseSections(md), [md]);

  // Scroll to the requested anchor (from URL or sidebar click). Resolves
  // short HelpLink/WelcomeTour anchors through ANCHOR_ALIASES first.
  useEffect(() => {
    const hash = location.hash?.replace("#", "");
    if (!hash || !md) return;
    const target = resolveAnchor(hash);
    const el = document.getElementById(target);
    if (el) {
      window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 80, behavior: "smooth" });
      setActiveAnchor(target);
    }
  }, [location.hash, md]);

  const filtered = useMemo(() => {
    if (!q.trim()) return sections;
    const needle = q.trim().toLowerCase();
    return sections.filter((s) => s.title.toLowerCase().includes(needle));
  }, [sections, q]);

  const onNavClick = (anchor) => {
    navigate(`/help#${anchor}`);
  };

  const onCopyLink = (anchor) => {
    const url = `${window.location.origin}/help#${anchor}`;
    navigator.clipboard?.writeText(url);
  };

  return (
    <div data-testid="help-page" className="grid grid-cols-12 gap-8">
      {/* Sidebar */}
      <aside className="col-span-12 lg:col-span-3">
        <div className="sticky top-24 space-y-4">
          <div>
            <div className="flex items-center gap-2 text-[10px] font-mono-ledger uppercase tracking-[0.25em] text-[#5C5F58] mb-1">
              <BookOpen size={12} /> The User&apos;s Guide
            </div>
            <h2 className="font-serif-display text-3xl text-[#0A192F] leading-tight">
              Read the manual.
            </h2>
          </div>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5F58]" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filter sections…"
              data-testid="help-search"
              className="w-full bg-white border border-[#E0DDD6] pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-[#1A3622]"
            />
          </div>
          <nav data-testid="help-nav" className="space-y-1 max-h-[60vh] overflow-y-auto pr-2">
            {filtered.map((s) => (
              <button
                key={s.anchor}
                onClick={() => onNavClick(s.anchor)}
                data-testid={`help-nav-${s.anchor}`}
                className={`w-full text-left px-3 py-1.5 text-sm transition-colors border-l-2 ${
                  activeAnchor === s.anchor
                    ? "border-l-[#C5A059] bg-[#F9F8F6] text-[#0A192F] font-medium"
                    : "border-l-transparent text-[#5C5F58] hover:text-[#0A192F] hover:bg-[#F9F8F6]"
                } ${s.depth === 3 ? "pl-7 text-xs" : ""}`}
              >
                {s.title}
              </button>
            ))}
          </nav>
          <a
            href={`${API}/help/user-guide-pdf`}
            data-testid="help-download"
            className="inline-flex items-center gap-2 border border-[#E0DDD6] text-[#5C5F58] px-3 py-2 text-xs hover:border-[#5C5F58]"
          >
            <FilePdf size={12} weight="bold" /> Download as PDF
          </a>
        </div>
      </aside>

      {/* Article */}
      <article className="col-span-12 lg:col-span-9 bg-white border border-[#E0DDD6] p-8 lg:p-12 max-w-[860px]">
        <div className="prose-arbor" data-testid="help-content">
          {md ? (
            <ReactMarkdown
              components={{
                h1: ({ node, ...props }) => <h1 className="font-serif-display text-4xl text-[#0A192F] mt-0 mb-6 leading-tight" {...props} />,
                h2: ({ node, children, ...props }) => {
                  // Mirror parseSections: strip the leading numbering so
                  // the rendered DOM id matches the sidebar URL hash.
                  const text = childrenToText(children).replace(/^\d+\.\s*/, "");
                  const anchor = slugify(text);
                  return (
                    <h2 id={anchor} className="font-serif-display text-2xl text-[#0A192F] mt-12 mb-4 pb-2 border-b border-[#E0DDD6] flex items-center gap-3 group" {...props}>
                      <span className="flex-1">{children}</span>
                      <button
                        onClick={() => onCopyLink(anchor)}
                        title="Copy link to this section"
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-[#5C5F58] hover:text-[#1A3622]"
                      >
                        <LinkIcon size={14} />
                      </button>
                    </h2>
                  );
                },
                h3: ({ node, ...props }) => <h3 className="font-serif-display text-lg text-[#0A192F] mt-8 mb-2" {...props} />,
                p: ({ node, children, ...props }) => {
                  // react-markdown wraps standalone images in <p>; rendering our
                  // custom <figure> inside <p> is invalid HTML. Detect the
                  // single-image case and unwrap to a fragment so the figure
                  // sits at block level.
                  const arr = Array.isArray(children) ? children : [children];
                  const onlyChild = arr.filter((c) => c !== "\n" && c !== "");
                  if (onlyChild.length === 1 && onlyChild[0]?.props?.node?.tagName === "img") {
                    return <>{children}</>;
                  }
                  return <p className="text-[15px] leading-[1.75] text-[#2F2F2D] my-4" {...props}>{children}</p>;
                },
                ul: ({ node, ...props }) => <ul className="my-4 ml-6 list-disc space-y-1 text-[15px] text-[#2F2F2D] leading-[1.7]" {...props} />,
                ol: ({ node, ...props }) => <ol className="my-4 ml-6 list-decimal space-y-1 text-[15px] text-[#2F2F2D] leading-[1.7]" {...props} />,
                li: ({ node, ...props }) => <li className="pl-1" {...props} />,
                strong: ({ node, ...props }) => <strong className="font-semibold text-[#0A192F]" {...props} />,
                em: ({ node, ...props }) => <em className="italic" {...props} />,
                code: ({ node, ...props }) => <code className="font-mono-ledger bg-[#F9F8F6] border border-[#E0DDD6] px-1.5 py-0.5 text-[12px] text-[#1A3622]" {...props} />,
                a: ({ node, href, ...props }) => {
                  if (href?.startsWith("#")) {
                    return <button onClick={() => onNavClick(href.replace("#", ""))} className="text-[#1A3622] underline underline-offset-2 hover:text-[#0F2818]" {...props} />;
                  }
                  return <a href={href} className="text-[#1A3622] underline underline-offset-2 hover:text-[#0F2818]" {...props} />;
                },
                img: ({ node, src, alt, ...props }) => (
                  <figure className="my-8 border border-[#E0DDD6] bg-[#F9F8F6]">
                    <img src={src} alt={alt} className="w-full" loading="lazy" {...props} />
                    {alt && (
                      <figcaption className="px-3 py-2 text-xs text-[#5C5F58] font-mono-ledger uppercase tracking-[0.2em] border-t border-[#E0DDD6]">
                        {alt}
                      </figcaption>
                    )}
                  </figure>
                ),
                blockquote: ({ node, ...props }) => (
                  <blockquote className="border-l-4 border-l-[#C5A059] bg-[#F9F8F6] px-5 py-3 my-5 italic text-[#5C5F58] font-serif-display text-[15px]" {...props} />
                ),
                table: ({ node, ...props }) => <div className="my-5 border border-[#E0DDD6] overflow-x-auto"><table className="w-full text-sm" {...props} /></div>,
                th: ({ node, ...props }) => <th className="text-left bg-[#F9F8F6] px-3 py-2 font-mono-ledger text-[10px] uppercase tracking-[0.2em] text-[#5C5F58] border-b border-[#E0DDD6]" {...props} />,
                td: ({ node, ...props }) => <td className="px-3 py-2 border-b border-[#F0EDE6] text-[#2F2F2D]" {...props} />,
                hr: () => <hr className="my-10 border-t border-[#E0DDD6]" />,
              }}
            >
              {md}
            </ReactMarkdown>
          ) : (
            <div className="font-mono-ledger text-xs uppercase tracking-[0.25em] text-[#5C5F58]">
              Loading guide…
            </div>
          )}
        </div>
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="mt-12 inline-flex items-center gap-2 text-xs text-[#5C5F58] hover:text-[#1A3622]"
          data-testid="help-back-to-top"
        >
          <ArrowUp size={12} /> Back to top
        </button>
      </article>
    </div>
  );
}

// --- helpers ---

/** Parse H2 headings from markdown and produce { title, anchor } pairs.
 * Anchors are slugified consistently with the h2 renderer so sidebar clicks
 * always find the right DOM node. */
function parseSections(md) {
  if (!md) return [];
  const out = [];
  for (const line of md.split("\n")) {
    const h2 = line.match(/^##\s+(.+)$/);
    if (h2) {
      // Strip the leading "N." numbering and markdown decoration
      const title = h2[1].replace(/^\d+\.\s*/, "").replace(/[`*]/g, "").trim();
      const anchor = slugify(title);
      out.push({ depth: 2, title, anchor });
    }
  }
  return out;
}

function slugify(s) {
  return String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

/** Best-effort: extract the text content from react-markdown's `children`
 * (which may be an array of strings + AST nodes). Used to compute the H2's
 * anchor id. */
function childrenToText(children) {
  if (children == null) return "";
  if (typeof children === "string") return children;
  if (Array.isArray(children)) return children.map(childrenToText).join("");
  if (children?.props?.children) return childrenToText(children.props.children);
  return "";
}
